import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';
import { API_URLS } from '../constants/apiUrl';
import { useLoading } from '../context/LoadingContext';
import { patch } from '../api/api';
import { showToast } from '../components/utils/toast';
import { IEditMatterRequestDto, IMatterRequest } from '../interface/matterRequest';
import { useAnalytics } from './useAnalytics';
import { AnalyticsFeatureOperation } from '../interface/analytics';
import { getMatterRequestUpdateErrorMessage } from '../helpers/matterRequestErrorHelper';
import { useMatterRequestMutationErrorHandler } from './useMatterRequestMutationErrorHandler';
import { isVersionConflictError } from '../helpers/versionConflictHelper';

const updateMatterRequest = async (
  id: string,
  editMatterRequestDto: IEditMatterRequestDto,
): Promise<IMatterRequest> => {
  return patch(`${API_URLS.CREATE_MATTER_REQUEST}/${id}`, editMatterRequestDto, {
    skipGlobalErrorHandler: true,
    skipForbiddenRedirect: true,
  });
};

export const useUpdateMatterRequest = (): UseMutationResult<
  IMatterRequest,
  AxiosError<{ message?: string; type?: string }>,
  { id: string; editMatterRequestDto: IEditMatterRequestDto }
> => {
  const { setIsLoading } = useLoading();
  const { t } = useTranslation();
  const { trackBusinessEvent } = useAnalytics();
  const handleError = useMatterRequestMutationErrorHandler((error) =>
    getMatterRequestUpdateErrorMessage(error, t),
  );

  return useMutation({
    mutationFn: ({ id, editMatterRequestDto }) => updateMatterRequest(id, editMatterRequestDto),
    onMutate: () => {
      setIsLoading(true);
    },
    onSuccess: () => {
      trackBusinessEvent(AnalyticsFeatureOperation.MATTER_REQUEST_CREATION);
      showToast('success', t('matterRequest:matterRequestEdited'));
    },
    onError: (error) => {
      if (isVersionConflictError(error)) return;
      handleError(error);
    },
    onSettled: () => {
      setIsLoading(false);
    },
  });
};
